import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { SocialPageService } from 'app/modules/social/social-page/services/social-page.service';
import { GuestAuthService } from 'app/services/guest-auth.service';
import { EventCalendar, generateGoogleCalendarLink, generateICS } from 'app/utils/generate-calendar-event';
import { Loggable } from 'app/utils/logging/loggable';
import SocialEvent from 'entity/SocialEvent';
import User from 'entity/User';
import UserRegistration from 'entity/UserRegistration';

import { RegistrationFormEmiter } from '../../components/registration-form/registration-form.component';
import { EventsService, RegistrationFieldQuestions } from '../../services/events.service';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';

const URL_REGISTRATION_SUFIX = '/registration';

@Component({
  selector: 'app-registration-page',
  templateUrl: './registration-page.component.html',
  styleUrl: './registration-page.component.scss',
  standalone: false,
})
export class RegistrationPageComponent extends Loggable implements OnDestroy, OnInit {
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected loading = false;
  protected event?: SocialEvent;
  protected registrationFields?: RegistrationFieldQuestions[];
  protected registrationCompleted = false;
  protected userRegistrationId?: string;
  private eventId?: string;
  public redirectToWelcomeRoom?: boolean;

  protected errorState = false;
  public currentYear?: number;
  private registrationId?: string;

  private loggedUser?: User;
  private currentDate?: Date;
  private eventsCountdown?: boolean;

  protected isEmbed = false;

  public constructor(
    private store: Store,
    private eventsService: EventsService,
    private guestAuthService: GuestAuthService,
    private socialPageService: SocialPageService,
    private route: ActivatedRoute,
    private videoChatService: VideoChatService,
    private router: Router,
  ) {
    super();
    this.currentDate = new Date();
    this.currentYear = this.currentDate.getFullYear();

    this.route.queryParams?.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      if (params?.embed) {
        this.isEmbed = params.embed;
      }
    });
  }

  public ngOnInit(): void {
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => (this.loggedUser = user));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.events_countdown)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((events_countdown) => {
        this.eventsCountdown = events_countdown;
      });

    void this.getSelectedEvent();
  }

  private async getSelectedEvent() {
    this.loading = true;

    let url = window.location.href;

    // remove GET vars
    // TODO: refactor to reactive data source, instead of using window.location, use ActivatedRoute, string munging is bad
    const getVarsIndex = url.indexOf('?');
    if (getVarsIndex !== -1) {
      url = url.substring(0, getVarsIndex);
    }

    const hasRegistrationEnd = url.split('/').pop();

    if (hasRegistrationEnd !== 'registration') {
      this.redirectToWelcomeRoom = true;

      this.registrationId = this.route.snapshot.queryParams['registrationId'];

      if (this.registrationId) {
        await this.fetchRegistrantsInfo();
        this.eventsService.registrationId = {
          endDateTime: this.event?.endDateTime,
          registrationId: this.registrationId,
        };
      }
    }

    const lastRegistrationIndex = url.lastIndexOf(URL_REGISTRATION_SUFIX);

    let eventLink: string | undefined;

    if (lastRegistrationIndex !== -1 && lastRegistrationIndex === url.length - URL_REGISTRATION_SUFIX.length) {
      eventLink = url.substring(0, lastRegistrationIndex);
    } else {
      eventLink = url;
    }

    if (!eventLink) {
      this.error('Error while parsing the event link');
      return;
    }

    // Used to submit registration form
    if (!this.registrationId) {
      await this.guestAuthService.registrationGuestSignIn(undefined, { firstName: 'Registration', lastName: 'User' });
    }

    // TODO: cleanup this code
    const parts = eventLink.split('/');
    if (parts[3] === 'web') {
      parts.splice(-4, 1);
      eventLink = parts.join('/');
    }
    this.eventsService
      .getEventsByLink(eventLink)
      .then(async (event) => {
        if (!event || !event.socialEventId) {
          return;
        }

        this.event = event;
        this.eventId = event.socialEventId;

        if (!this.redirectToWelcomeRoom) {
          this.registrationFields = await this.eventsService.getFormRegistrationField(event);
          this.loading = false;
          return;
        }

        // Event has countdown
        if (this.eventsCountdown && event.startDateTime && this.currentDate && this.currentDate < event.startDateTime) {
          this.redirectToCountdownPage(event.socialEventId).catch((error) => {
            this.error('Error redirecting to countdown page -', error);
            this.loading = false;
          });
          return;
        }

        this.redirectToWelcomePage().catch((error) => {
          this.error('Error redirecting to room -', error);
          this.loading = false;
        });
      })
      .catch((error) => {
        this.error(error);
        this.loading = false;
        this.errorState = true;
      });
  }

  protected saveRegistrant(formResult: RegistrationFormEmiter) {
    this.info('save registration', formResult);

    if (!this.eventId) {
      return;
    }

    const resultArray = Object.entries(formResult)
      .filter(([, value]) => value !== undefined && !!value.toString().trim().length)
      .map(([key, value]) => ({
        name: key,
        value: value.toString(),
      }));

    this.eventsService
      .saveRegistration(this.eventId, resultArray)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((registrationId) => {
        this.registrationCompleted = true;
        this.userRegistrationId = registrationId;
        this.guestAuthService.reinitAuth();
      });
  }

  protected saveCalendar(type: 'google' | 'outlook') {
    if (!this.event) {
      return;
    }

    const eventData: EventCalendar = {
      uid: this.event.socialEventId ?? '',
      summary: this.event.name ?? '',
      description: `${this.event.description ?? ''}, ${this.event.eventLink ?? ''}?registrationId=${this.userRegistrationId ?? ''}`,
      startDate: new Date(this.event.startDateTime ?? ''),
      endDate: new Date(this.event.endDateTime ?? ''),
      location: `${this.event.eventLink ?? ''}?registrationId=${this.userRegistrationId ?? ''}`,
    };

    const content = type === 'google' ? generateGoogleCalendarLink(eventData) : generateICS(eventData);

    if (type === 'outlook') {
      const blob = new Blob([content], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      return;
    }

    window.open(content, '_blank');
  }

  public ngOnDestroy(): void {
    if (!this.registrationId) {
      this.guestAuthService.reinitAuth();
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private async redirectToWelcomePage(): Promise<void> {
    if (this.event && this.event.socialRoomSetId) {
      const socialRoomSet = await firstValueFrom(this.videoChatService.getSocialRoomSet(this.event.socialRoomSetId));

      if (!socialRoomSet || !socialRoomSet.socials || socialRoomSet.socials.length === 0) {
        this.error('No SocialRoomSet was found');
        return;
      }

      this.socialPageService
        .onEnterRoom(socialRoomSet.socials[0], this.registrationId, this.event.name, this.event.endDateTime)
        .catch((e) => this.error(e));
    }
  }

  private async redirectToCountdownPage(eventId: string): Promise<void> {
    await this.router.navigate([`events/${eventId}/countdown`]);
  }

  private async executeSignIn(registration: UserRegistration): Promise<void> {
    if (!registration || !registration?.registrationId) {
      this.info('Transfer Info: registrationId not found, skipping process');
      return;
    }

    if (!this.loggedUser) {
      this.info('Transfer Info: Account not found. Creating a new guest.');
      await this.guestAuthService.registrationGuestSignIn({
        user: registration.user,
        bearer: registration.guestBearer,
        refreshBearer: registration.refreshBearer,
      });
      return;
    }

    const isSameUser = this.loggedUser?.userId === registration.user?.userId;

    if (isSameUser) {
      this.info('Transfer Info: Account is the same for registration, proceeding.');
      return;
    }

    this.info('Transfer Info: Transferring registration info...');
    this.eventsService
      .transferRegistration(registration.registrationId)
      .then(() => this.info('Transfer Info: Registration transferred to logged user successfully'))
      .catch((err) => this.info('Transfer Info: Error transferring registration to logged user:', err));
  }

  private async fetchRegistrantsInfo(): Promise<void> {
    await this.eventsService
      .getRegistrantInfo(this.registrationId ?? '')
      .then(async (registration) => {
        this.event = registration.event;
        await this.executeSignIn(registration);
      })
      .catch();
  }
}
